import { defineStore } from "pinia";
import { ref } from "vue";
import { useRoute } from "vue-router";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import { getBIGeneralBetratio } from "@/api/bigeneral-api";
import usePrematchBetSpread from "@/prematch/composables/use-prematch-bet-spread";
import type { EventDetail } from "@/prematch/model/prematch-event-detail";
import { EventDrilldown, EventDrilldownDetail } from "@/shared/model/event";
import usePrematchRouteIds from "../composables/use-prematch-route-ids";
import useTrefikApi from "@/api/composables/use-trefik-api";
import { logException } from "@/shared/utils/logger/use-app-logger";

export const usePrematchEvents = defineStore("usePrematchEvents", () => {
    const route = useRoute();

    const { getRouteIds } = usePrematchRouteIds();
    const { getEventsDrilldownDetail, getPregenerated } = useOpenbetApi();
    const { normalizeBetData } = usePrematchBetSpread();
    const { getTrefikMatchStats } = useTrefikApi();

    const eventDetails = ref<Record<string, EventDetail<EventDrilldownDetail>>>({});
    const failedEvents = ref<string[]>([]);

    const canRenderThisUrl = <T extends EventDrilldown>(event: T) => {
        if (!event) return false;
        const sportId = event.drilldownNodes[2].id;
        const leagueId = event.drilldownNodes[0].id;
        if (sportId != getRouteIds.value.sportId || leagueId != getRouteIds.value.leagueId) return false;

        return true;
    };

    async function fetchDataAndSaveToEventDetails(eventId: string) {
        try {
            const safePromise = <T>(promise: Promise<T>): Promise<T | null> =>
                promise.catch((error) => {
                    logException("prematch-detail", error, {
                        message: "An error occurred while fetching event details",
                        eventId: eventId,
                    });
                    return null;
                });

            const [eventsDetail, betRatioResponse, analysis, stats] = await Promise.all([
                safePromise(getEventsDrilldownDetail({ eventIds: [Number(eventId)], groupedMarkets: true })),
                safePromise(getBIGeneralBetratio({ id: eventId })),
                safePromise(getPregenerated({ matchIds: [eventId] })),
                safePromise(getTrefikMatchStats({ event: eventId })),
            ]);

            if (route.name === "PrematchEventDetail" && eventsDetail && !canRenderThisUrl(eventsDetail[0])) {
                failedEvents.value.push(eventId);
                throw new Error("404");
            }

            eventDetails.value = {
                ...eventDetails.value,
                [eventId]: {
                    event: eventsDetail?.[0] || null,
                    betRatio: betRatioResponse
                        ? normalizeBetData(betRatioResponse?.[0]?.data, eventsDetail?.[0])
                        : null,
                    analysis: analysis || null,
                    trefik: stats || null,
                },
            };
        } catch (error) {
            logException("prematch-detail", error, {
                message: "An error occurred while fetching event details",
                eventId: eventId,
            });
            failedEvents.value.push(eventId);
        }
    }

    // watch(
    //     [() => route.params.eventId, () => route.query.eventId],
    //     async ([paramsEventId, queryEventId], [oldParamsEventId, oldQueryEventId]) => {
    //         if (paramsEventId && paramsEventId !== oldParamsEventId) {
    //             await fetchDataAndSaveToEventDetails(paramsEventId as string);
    //         }
    //         if (queryEventId && queryEventId !== oldQueryEventId) {
    //             await fetchDataAndSaveToEventDetails(queryEventId as string);
    //         }
    //     },
    //     { immediate: true, deep: true },
    // );

    return {
        eventDetails,
        failedEvents,
        fetchDataAndSaveToEventDetails,
    };
});
